import { ArrowRight } from "lucide-react";
import { StatusDot } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { formatNumber } from "@/lib/utils";
import { MOCK_AGENT_PULSE } from "@/components/mocks/fixtures";
import type { DashboardAgentPulse } from "@/lib/dashboard";

interface Props {
  data?: DashboardAgentPulse;
}

/**
 * THE sales screen. Surfaces the autonomous agent's real-time output so the
 * brand operator feels they're watching a living system, not reading a report.
 */
export function AgentsWorking({ data }: Props = {}) {
  const pulse = data ?? {
    in_flight: MOCK_AGENT_PULSE.inFlight,
    last_hour: MOCK_AGENT_PULSE.lastHour,
    today_counts: MOCK_AGENT_PULSE.todayCounts,
  };
  const { in_flight, last_hour, today_counts } = pulse;

  return (
    <Card
      surface="base"
      className="relative overflow-hidden p-7"
      style={{
        background:
          "radial-gradient(120% 80% at 0% 0%, rgba(86,194,255,0.06), rgba(7,8,10,0) 60%)," +
          "var(--color-deep-charcoal)",
      }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-slate-200)] flex items-center gap-2">
            <StatusDot tone="ember" /> Your agents are working
          </p>
          <div className="mt-3 flex items-baseline gap-6 flex-wrap">
            <Stat label="In flight now" value={in_flight} />
            <Stat label="Last hour" value={last_hour} />
          </div>
        </div>

        <a
          href="/outreach"
          className="inline-flex items-center gap-1 text-[13px] text-[var(--color-snow)] hover:text-[var(--color-sky)] transition-colors"
        >
          See live outreach feed
          <ArrowRight size={14} />
        </a>
      </div>

      <div className="mt-6">
        <p className="text-[14px] text-[var(--color-slate-200)] mb-3">
          Today the agent…
        </p>
        <ul className="space-y-2 text-[15px] text-[var(--color-snow)]">
          <Bullet>
            created <Count>{formatNumber(today_counts.artifacts)}</Count>{" "}
            personalized artifacts
          </Bullet>
          <Bullet>
            opened <Count>{formatNumber(today_counts.agendas_opened)}</Count>{" "}
            new agendas on member goals
          </Bullet>
          <Bullet>
            sent <Count>{formatNumber(today_counts.outreach)}</Count> outreach
            messages
          </Bullet>
          <Bullet>
            dropped <Count>{formatNumber(today_counts.agendas_dropped)}</Count>{" "}
            stale agendas
          </Bullet>
        </ul>
      </div>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-slate-300)]">
        {label}
      </p>
      <p className="text-heading text-[var(--color-snow)] font-semibold leading-none mt-0.5">
        {formatNumber(value)}
      </p>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span
        aria-hidden
        className="mt-2.5 inline-block h-1 w-1 rounded-full bg-[var(--color-slate-300)]"
      />
      <span>{children}</span>
    </li>
  );
}

function Count({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono text-[var(--color-snow)] font-medium">
      {children}
    </span>
  );
}
